import { useState, useSyncExternalStore } from 'react';
import styled from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress';
import Card from './ui/Card';
import { GoldButton, OutlineButton } from './ui/Buttons';
import { ui } from './ui/tokens';
import SaveOnlineIcon from '../assets/design/save-online.svg';
import SaveFolderGraphic from '../assets/design/save-folder.svg';
import { saveOnlineAndClose } from '../helpers/saveOnline';
import { dismissSaveReminder, isSaveReminderDismissed, isSaving, onSavingChange, saveAndContinue, supportsSaveAndContinue } from '../helpers/saveSession';
import { lockCanvasInput, unlockCanvasInput } from '../libs/BitmapEditor/bitmapMaker3';

type Dialog = 'reminder' | 'notice' | null;

/**
 * The SAVE tab on the bottom edge of the canvas (GUI route 11, "Save while
 * working on a creation"). With an app that supports it, saving keeps the
 * editor open; the first saves show a reminder that can be switched off.
 * Older app versions fall back to save & close.
 */
function FloatingSave() {
	const [saving, setSaving] = useState(false);
	const [dialog, setDialog] = useState<Dialog>(null);
	// Also true during a background auto-save, which leaves the tab pressable
	const saveInProgress = useSyncExternalStore(onSavingChange, isSaving);

	// The tab sits on top of the canvas and p5 listens on the window, so a press
	// here would also start a pencil stroke. Pointer events fire before p5's
	// touch/mouse handlers: lock input on press, release it once the press ends.
	const handlePointerDown = () => {
		lockCanvasInput();
		const release = () => {
			window.removeEventListener('pointerup', release);
			window.removeEventListener('pointercancel', release);
			setTimeout(unlockCanvasInput, 0);
		};
		window.addEventListener('pointerup', release);
		window.addEventListener('pointercancel', release);
	};

	const runSave = async (save: () => Promise<void>) => {
		if (saving) return;
		setSaving(true);
		try {
			await save();
		} catch (error: any) {
			console.error(error);
			alert(`Error saving image: \n ${error?.message ?? error}`);
		} finally {
			setSaving(false);
		}
	};

	const handlePress = () => {
		if (!supportsSaveAndContinue()) {
			runSave(saveOnlineAndClose);
			return;
		}
		if (!isSaveReminderDismissed()) {
			setDialog('reminder');
			return;
		}
		runSave(saveAndContinue);
	};

	const handleSaveAndContinue = () => {
		setDialog(null);
		runSave(saveAndContinue);
	};

	const handleDontShowAgain = () => {
		dismissSaveReminder();
		setDialog('notice');
	};

	// The app's progress overlay would cover the notice, so save once it closes
	const handleNoticeClose = () => {
		setDialog(null);
		runSave(saveAndContinue);
	};

	return (
		<>
			<Tab type="button" onPointerDown={handlePointerDown} onClick={handlePress} aria-label="Save" disabled={saving}>
				{saving || saveInProgress ? <CircularProgress size={22} sx={{ color: ui.gold }} /> : <img src={SaveOnlineIcon} alt="" width={30} height={30} draggable={false} />}
				<Label>Save</Label>
			</Tab>

			<Card
				open={dialog === 'reminder'}
				title="Save your creation"
				onClose={() => setDialog(null)}
				description="Save your work to My Pixels to avoid losing your work in progress. It stays unpublished until you choose to publish it."
			>
				<Graphic src={SaveFolderGraphic} alt="" />
				<Stack>
					<GoldButton type="button" onClick={handleSaveAndContinue}>
						Save & continue
					</GoldButton>
					<OutlineButton type="button" onClick={handleDontShowAgain}>
						Don't show this message again
					</OutlineButton>
				</Stack>
			</Card>

			<Card open={dialog === 'notice'} title="Save reminders are off" onClose={handleNoticeClose}>
				<Notice>
					<img src={SaveFolderGraphic} alt="" width={72} height={72} />
					<p>
						You will no longer receive save reminders. Please press the save button regularly or set an auto-save time limit in your profile settings to avoid losing progress you've made
						with your creation.
					</p>
				</Notice>
			</Card>
		</>
	);
}

export default FloatingSave;

const Tab = styled.button`
	position: absolute;
	left: 50%;
	bottom: 0;
	transform: translateX(-50%);
	width: 64px;
	padding: 8px 0 6px;
	border: none;
	border-radius: 32px 32px 0 0;
	background-color: ${ui.page};
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2px;
	cursor: pointer;
	z-index: 2;
	-webkit-tap-highlight-color: transparent;

	&:disabled {
		cursor: wait;
	}
`;

const Label = styled.span`
	font-family: ${ui.font};
	font-size: 9px;
	font-weight: 700;
	text-transform: uppercase;
	color: ${ui.label};
`;

const Graphic = styled.img`
	display: block;
	width: 72px;
	height: 72px;
	margin: 20px auto 4px;
`;

const Stack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-top: 20px;

	button {
		flex: none; /* the shared buttons flex for rows; keep their height here */
	}
`;

const Notice = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	margin-top: 16px;

	p {
		margin: 0;
		font-family: ${ui.font};
		font-size: 14px;
		font-weight: 600;
		line-height: 1.45;
	}
`;
